'use client';

import { useState, useCallback, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import SajuForm from '@/components/SajuForm';
import ResultCard from '@/components/ResultCard';
import ProgressStepper from '@/components/ProgressStepper';
import PayActionPayment from '@/components/PayActionPayment';
import PreviewLite from '@/components/PreviewLite';
import type { CalculateResponse, InterpretResponse, ConcernType } from '@/types';
import type { SurveyData } from '@/components/BusinessSurvey';
import { calculateSaju, startReportGeneration } from '@/lib/api';

type Step = 'input' | 'calculating' | 'generating' | 'result' | 'payment';

export default function Home() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    }>
      <HomeContent />
    </Suspense>
  );
}

function HomeContent() {
  const BRAND_NAME = process.env.NEXT_PUBLIC_BRAND_NAME ?? '사주OS';
  const BRAND_TAGLINE = process.env.NEXT_PUBLIC_BRAND_TAGLINE ?? '2026년, 당신의 중요한 결정을 대신 정리해드립니다';

  const searchParams = useSearchParams();
  const router = useRouter();

  // 🔐 어드민 프리패스: URL에 admin=1 파라미터 존재 여부
  const isAdminParam = searchParams.get('admin') === '1';
  const ADMIN_EMAIL = 'pdjuck@naver.com';

  const [step, setStep] = useState<Step>('input');
  const [reportId, setReportId] = useState<string | null>(null);
  const [calculateResult, setCalculateResult] = useState<CalculateResponse | null>(null);
  const [interpretResult, setInterpretResult] = useState<InterpretResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAdminMode, setIsAdminMode] = useState(false);

  // 🔥 P0: 결제 UX용 상태
  const [pendingFormData, setPendingFormData] = useState<{
    name: string;
    email: string;
    birthYear: number;
    birthMonth: number;
    birthDay: number;
    birthHour: number | null;
    birthMinute: number;
    gender: 'male' | 'female' | 'other';
    concernType: ConcernType;
    question: string;
    surveyData?: SurveyData;
    calcResult?: CalculateResponse;
  } | null>(null);

  /**
   * 🔥 P0: 리포트 생성 시작 (상태 머신 기반)
   * - 사주 계산 → 리포트 생성 API 호출 → HTTP status 분기
   */
  const handleSubmit = async (formData: {
    name: string;
    email: string;
    birthYear: number;
    birthMonth: number;
    birthDay: number;
    birthHour: number | null;
    birthMinute: number;
    gender: 'male' | 'female' | 'other';
    concernType: ConcernType;
    question: string;
    surveyData?: SurveyData;
  }) => {
    setStep('calculating');
    setError(null);
    setReportId(null);

    try {
      // 1. 사주 계산 (절기 기반)
      const calcResult = await calculateSaju({
        birth_year: formData.birthYear,
        birth_month: formData.birthMonth,
        birth_day: formData.birthDay,
        birth_hour: formData.birthHour,
        birth_minute: formData.birthMinute,
        gender: formData.gender,
      });
      setCalculateResult(calcResult);

      // 🔥 입력 데이터 저장 (결제 후 재시도용)
      setPendingFormData({ ...formData, calcResult });

      // 🔐 어드민 프리패스: admin=1 + pdjuck@naver.com 이중 검증
      if (isAdminParam && formData.email.trim().toLowerCase() === ADMIN_EMAIL) {
        console.log('[SajuOS] 🔓 어드민 프리패스 활성화 - 결제 모달 건너뜀');
        setIsAdminMode(true);
        setStep('generating');

        // 결제 없이 /api/v1/reports/start 직접 호출
        const result = await startReportGeneration({
          email: formData.email,
          name: formData.name,
          saju_result: calcResult,
          year_pillar: calcResult.saju.year_pillar.ganji,
          month_pillar: calcResult.saju.month_pillar.ganji,
          day_pillar: calcResult.saju.day_pillar.ganji,
          hour_pillar: calcResult.saju.hour_pillar?.ganji,
          target_year: 2026,
          question: formData.question,
          concern_type: formData.concernType,
          survey_data: formData.surveyData as any,
        });

        if (result.httpStatus === 200 && result.data) {
          console.log('[SajuOS] ✅ 어드민 리포트 생성 시작:', result.data.job_id);
          router.push(`/report/public/${result.data.job_id}`);
          return;
        }

        // 실패 시 에러 표시
        console.error('[SajuOS] ❌ 어드민 리포트 생성 실패:', result.detail);
        setError(result.detail || '리포트 생성에 실패했습니다.');
        setStep('input');
        return;
      }

      // 2. 일반 사용자: 결제 단계로 진입
      //    ⚠️ pending 상태에서 reports/start 호출 금지
      //    결제 확인(paid) 후 백엔드가 report job을 자동 생성함
      setStep('payment');

    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.';
      setError(errorMessage);
      setStep('input');
    }
  };

  /**
   * 결제 오류 처리
   */
  const handlePaymentError = useCallback((errorMsg: string) => {
    console.error('[SajuOS] ❌ 결제 오류:', errorMsg);
    setError(errorMsg);
    setStep('input');
  }, []);

  const handleReportComplete = (result: any) => {
    // 폴링 완료 시 결과 설정
    setInterpretResult(result);
    localStorage.removeItem('sajuos_report_id');
    setStep('result');
  };

  const handleReportError = (errorMsg: string) => {
    setError(errorMsg);
    // 에러 시에도 재시도 가능하도록 step은 유지
  };

  const handleReset = () => {
    setStep('input');
    setReportId(null);
    setCalculateResult(null);
    setInterpretResult(null);
    setError(null);
    setPendingFormData(null);  // 🔥 P0: 결제 데이터도 초기화
    localStorage.removeItem('sajuos_report_id');
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <header className="text-center py-8">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-amber-500 bg-clip-text text-transparent mb-3">
          🔮 {BRAND_NAME}
        </h1>
        <p className="text-slate-700 text-lg">{BRAND_TAGLINE}</p>
      </header>

      {/* 🔐 어드민 모드 안내: admin=1 + 이메일 검증 완료 후에만 표시 */}
      {isAdminMode && (
        <div className="bg-indigo-50 border border-indigo-200 text-indigo-700 px-4 py-2 rounded-lg text-sm text-center">
          관리자 모드입니다. 결제 없이 리포트가 생성됩니다.
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg animate-fade-in-up">
          <div className="flex items-start gap-3">
            <span className="text-xl">⚠️</span>
            <div>
              <p className="font-medium">오류 발생</p>
              <p className="text-sm mt-1">{error}</p>
              <p className="text-xs text-red-500 mt-2">
                네트워크 연결과 서버 상태를 확인해주세요.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Step: Input Form */}
      {step === 'input' && <SajuForm onSubmit={handleSubmit} />}

      {/* Step: Calculating (사주 계산 중) */}
      {step === 'calculating' && (
        <div className="flex flex-col items-center justify-center py-20 animate-fade-in-up">
          <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mb-6" />
          <p className="text-xl font-medium text-slate-700">사주 원국 계산 중...</p>
          <p className="text-slate-500 mt-2">절기 기반 정확한 계산 🌟</p>
        </div>
      )}

      {/* 🔥 P0: Step: Payment (Preview 배경 + 결제 모달 오버레이) */}
      {step === 'payment' && pendingFormData && pendingFormData.calcResult && (
        <>
          {/* 🔥 Preview 배경 (결제 모달 뒤에서도 보임) */}
          <div className="animate-fade-in-up">
            <PreviewLite
              calcResult={pendingFormData.calcResult}
              name={pendingFormData.name}
              targetYear={2026}
            />
          </div>

          {/* 🔥 결제 모달 오버레이 (페이지 이동 없이 컨텍스트 유지) */}
          <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* 반투명 배경 */}
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />

            {/* 모달 컨테이너 */}
            <div className="relative min-h-screen flex items-center justify-center p-4">
              <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                {/* 모달 헤더 */}
                <div className="sticky top-0 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 p-4 rounded-t-2xl">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">🔒</span>
                    <div>
                      <h3 className="font-bold text-amber-800 text-lg">결제가 필요합니다</h3>
                      <p className="text-amber-700 text-sm">
                        결제 후 바로 이어서 리포트가 생성됩니다
                      </p>
                    </div>
                  </div>
                </div>

                {/* 결제 컴포넌트 */}
                <div className="p-4">
                  <PayActionPayment
                    userEmail={pendingFormData.email}
                    userName={pendingFormData.name}
                    amount={19000}
                    inputData={{
                      ...pendingFormData,
                      saju_result: pendingFormData.calcResult,
                    }}
                    onError={handlePaymentError}
                  />
                </div>

                {/* 취소 버튼 */}
                <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 rounded-b-2xl">
                  <button
                    onClick={handleReset}
                    className="w-full text-slate-500 hover:text-slate-700 text-sm py-2"
                  >
                    처음으로 돌아가기
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Step: Generating (폴링 기반 실시간 진행) */}
      {step === 'generating' && (
        <div className="animate-fade-in-up">
          <ProgressStepper
            reportId={reportId}
            onComplete={handleReportComplete}
            onError={handleReportError}
          />
          
          {/* 재시도 버튼 (에러 발생 시) */}
          {error && (
            <div className="mt-4 text-center">
              <button
                onClick={handleReset}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition"
              >
                처음부터 다시하기
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step: Result */}
      {step === 'result' && calculateResult && interpretResult && (
        <ResultCard
          calculateResult={calculateResult}
          interpretResult={interpretResult}
          onReset={handleReset}
        />
      )}
    </div>
  );
}
